import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import { ParentSize } from '@visx/responsive';
import { scaleTime, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { LinePath, Bar } from '@visx/shape';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { bisector } from 'd3-array';
import dayjs from 'dayjs';

import { calculateXAxisTickValues, tickFormatFactory } from '../../utils/chartHelpers';
import { DATE_FORMAT_WEEKDAY_MONTH_DAY } from '../../utils/dates';
import { convertGramsToSmallUnits, weightSmallUnitLabel } from '../../utils/unitConversion';
import { useAtomValue } from 'jotai';
import { algorithmVersionAtom } from '../../utils/jotaiAtoms';
import useUser from '../../utils/hooks/useUser';
import { OpenEyeIcon, ClosedEyeIcon } from '../../atoms/Icons';

import './FeedLinePerformanceChart.scss';

const TOOLTIP_STYLES = {
  ...defaultStyles,
  borderRadius: '8px',
  backgroundColor: 'rgba(40, 40, 40, 0.8)',
  color: 'white',
  border: '1px solid #E6E6E6',
  padding: '1rem',
};

// Data accessors for {t, v} format where t = unix timestamp, v = value
export const getX = (point) => point.t; // Convert unix seconds to milliseconds for Date
export const getY = (point) => point.v;

const FEEDLINE_PERFORMANCE_GQL = gql`
  query FeedLinePerformanceQuery(
    $feedLineId: uuid!
    $start: bigint!
    $end: bigint!
    $interval: String!
    $time_zone: String!
    $alg: String
  ) {
    feed_line_by_pk(id: $feedLineId) {
      id
      name
      feed_frame_summaries: feed_frame_summaries_v2(
        args: { p_after: $start, p_before: $end, p_interval: $interval, p_time_zone: $time_zone, p_alg: $alg }
      ) {
        start_timestamp
        end_timestamp
        total_consumption
        total_duration
        total_feed_system_runs
      }
    }
  }
`;

function FeedLinePerformanceChart({
  feedLineId = '',
  isMetric = false,
  chartHeight = 200,
  chartMargin = { top: 20, bottom: 25, left: 75, right: 25 },
  chartStartTime = 0,
  chartEndTime = 0,
  loading = false,
}) {
  const { tooltipData, tooltipLeft = 0, tooltipTop = 0, tooltipOpen, showTooltip, hideTooltip } = useTooltip();
  const algorithmVersion = useAtomValue(algorithmVersionAtom);
  const { user } = useUser();

  // State for hiding/showing chart elements
  const [showMassPerMinute, setShowMassPerMinute] = useState(true);
  const [showAugerRuns, setShowAugerRuns] = useState(false);
  const [showRunningTime, setShowRunningTime] = useState(true);

  // Calculate 4-week time range
  const { start, end } = useMemo(() => {
    if (chartStartTime && chartEndTime) {
      return { start: chartStartTime, end: chartEndTime };
    }

    const now = dayjs.tz().startOf('day').unix();
    const fourWeeksAgo = now - 4 * 7 * 24 * 60 * 60;
    return { start: fourWeeksAgo, end: now };
  }, [chartStartTime, chartEndTime]);

  // Query feedline performance data
  const { loading: dataLoading, data: feedLineData } = useQuery(FEEDLINE_PERFORMANCE_GQL, {
    variables: {
      feedLineId,
      start: Math.floor(start), // Ensure it's an integer
      end: Math.floor(end), // Ensure it's an integer
      interval: 'day', // Daily intervals for 4-week data
      time_zone: user?.timezone || 'UTC',
      ...(algorithmVersion ? { alg: algorithmVersion } : {}),
    },
    skip: !feedLineId,
  });

  // Process feedframe data into chart format
  const chartData = useMemo(() => {
    if (!feedLineData?.feed_line_by_pk?.feed_frame_summaries?.length) {
      return [];
    }

    const summaries = feedLineData.feed_line_by_pk.feed_frame_summaries;

    return summaries
      .map((summary) => {
        const massInGrams = summary.total_consumption;
        const durationInMinutes = summary.total_duration / 60; // Convert seconds to minutes
        const massPerMinute = durationInMinutes > 0 ? massInGrams / durationInMinutes : 0;

        const result = {
          timestamp: summary.start_timestamp,
          massPerMinute,
          duration: summary.total_duration,
          massInGrams,
          numAugerRuns: summary.total_feed_system_runs,
          // Add time period info for context
          periodStart: summary.start_timestamp,
          periodEnd: summary.end_timestamp,
        };

        return result;
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [feedLineData, feedLineId]);

  // Filter out null days only for stats calculation
  const statsData = useMemo(() => {
    return chartData.filter((point) => {
      // Filter out null days where all inputs are 0
      return !(point.massPerMinute === 0 && point.duration === 0 && point.numAugerRuns === 0);
    });
  }, [chartData]);

  // Transform data for line chart (mass per minute)
  const massPerMinuteData = useMemo(() => {
    return chartData.map((point) => ({
      t: point.timestamp,
      v: point.massPerMinute,
    }));
  }, [chartData]);

  // Transform data for bars (auger runs)
  const augerRunsData = useMemo(() => {
    return chartData.map((point) => ({
      t: point.timestamp,
      v: point.numAugerRuns,
    }));
  }, [chartData]);

  // Transform data for secondary axis (running time in minutes)
  const runningTimeData = useMemo(() => {
    return chartData.map((point) => ({
      t: point.timestamp,
      v: point.duration / 60, // Convert seconds to minutes
    }));
  }, [chartData]);

  const unitLabel = weightSmallUnitLabel(isMetric);
  const statsConfig = useMemo(
    () => [
      {
        id: 'massPerMinute',
        label: 'Flow Rate',
        color: '#26AF5F',
        isVisible: showMassPerMinute,
        toggleVisibility: () => setShowMassPerMinute(!showMassPerMinute),
        getValue: () =>
          statsData.length > 0
            ? `${convertGramsToSmallUnits(
                isMetric,
                statsData.reduce((sum, d) => sum + d.massPerMinute, 0) / statsData.length,
              )} ${unitLabel}/min `
            : 'N/A',
        toggleTitle: showMassPerMinute ? 'Hide Flow Rate' : 'Show Flow Rate',
      },
      {
        id: 'runningTime',
        label: 'Runtime',
        color: '#217BCE',
        isVisible: showRunningTime,
        toggleVisibility: () => setShowRunningTime(!showRunningTime),
        getValue: () =>
          statsData.length > 0
            ? `${Math.round(statsData.reduce((sum, d) => sum + d.duration, 0) / 60 / statsData.length)} min/day`
            : 'N/A',
        toggleTitle: showRunningTime ? 'Hide Runtime' : 'Show Runtime',
      },
      {
        id: 'augerRuns',
        label: 'Auger Runs',
        color: 'rgba(163, 206, 182, 1.0)',
        isVisible: showAugerRuns,
        toggleVisibility: () => setShowAugerRuns(!showAugerRuns),
        getValue: () =>
          statsData.length > 0
            ? (statsData.reduce((sum, d) => sum + d.numAugerRuns, 0) / statsData.length).toFixed(1) + '/day'
            : 'N/A',
        toggleTitle: showAugerRuns ? 'Hide Auger Runs' : 'Show Auger Runs',
      },
    ],
    [statsData, isMetric, unitLabel, showAugerRuns, showMassPerMinute, showRunningTime],
  );

  // Helper function to render a single stat item
  const renderStatItem = useCallback(
    (config) => (
      <div key={config.id} className="FeedLinePerformanceChart-summaryItem">
        <div className="FeedLinePerformanceChart-colorIndicator" style={{ backgroundColor: config.color }}></div>
        <button
          className="FeedLinePerformanceChart-toggleButton"
          onClick={config.toggleVisibility}
          title={config.toggleTitle}
        >
          {config.isVisible ? (
            <OpenEyeIcon style={{ width: '16px', height: '16px' }} />
          ) : (
            <ClosedEyeIcon style={{ width: '16px', height: '16px' }} />
          )}
        </button>
        <div className="FeedLinePerformanceChart-contentWrapper">
          <span className="FeedLinePerformanceChart-summaryLabel">{config.label}</span>
          <span className="FeedLinePerformanceChart-summaryValue">{config.getValue()}</span>
        </div>
      </div>
    ),
    [],
  );

  const xAxisTicks = calculateXAxisTickValues(start, end);
  const xAxisTickFormat = tickFormatFactory(start, end);

  // Filter out tick values that are beyond our data range
  const filteredTicks = xAxisTicks.filter((tick) => tick >= start && tick <= end);

  // Only show labels for every 2nd day, but keep all ticks for grid lines
  const labelTicks = filteredTicks.filter((_, index) => index % 2 === 0);

  const handleTooltip = useCallback(
    (event) => {
      const { x } = localPoint(event) || { x: 0 };

      // Create scales for tooltip calculation
      const xMax = event.currentTarget.clientWidth - chartMargin.left - chartMargin.right;
      const xScale = scaleTime({
        domain: [start, end],
        range: [0, xMax],
      });

      // Convert mouse position to timestamp
      const mouseX = x - chartMargin.left;
      const timestamp = xScale.invert(mouseX);

      // Use D3 bisector to find the closest data point
      const bisectTime = bisector((point) => point.t).center;

      const index = bisectTime(massPerMinuteData, timestamp);
      const point = massPerMinuteData[index];

      if (!point) return;

      // Only show tooltip if we're within a reasonable distance (6 hours)
      if (Math.abs(point.t - timestamp) > 60 * 60 * 6) return;

      const chartDataPoint = chartData[index];
      if (chartDataPoint) {
        showTooltip({
          tooltipData: chartDataPoint,
          tooltipLeft: x,
          tooltipTop: chartHeight / 2,
        });
      }
    },
    [chartData, start, end, chartHeight, chartMargin, showTooltip, massPerMinuteData],
  );

  if (loading || dataLoading) {
    return (
      <div className="FeedLinePerformanceChart">
        <div className="FeedLinePerformanceChart-loading">Loading chart...</div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="FeedLinePerformanceChart">
        <div className="FeedLinePerformanceChart-noData">
          No feed data available for this feedline
          <br />
          <small>Debug: feedLineId = {feedLineId}</small>
        </div>
      </div>
    );
  }

  return (
    <div className="FeedLinePerformanceChart">
      <div className="FeedLinePerformanceChart-mainLayout">
        <div className="FeedLinePerformanceChart-chartContainer">
          <ParentSize debounceTime={10}>
            {(parent) => {
              const xMax = parent.width - chartMargin.left - chartMargin.right;
              const yMax = chartHeight - chartMargin.top - chartMargin.bottom;

              const xScale = scaleTime({
                domain: [start, end],
                range: [0, xMax],
              });

              // Primary Y scale for mass per minute
              const yScale = scaleLinear({
                domain: [0, Math.max(...massPerMinuteData.map(getY)) * 1.2],
                range: [yMax, 0],
              });

              // Secondary Y scale for auger runs (bars)
              const yScaleBars = scaleLinear({
                domain: [0, Math.max(...augerRunsData.map(getY)) * 1.2],
                range: [yMax, yMax / 2],
              });

              // Secondary Y scale for running time
              const yScaleSecondary = scaleLinear({
                domain: [0, Math.max(...runningTimeData.map(getY)) * 1.2],
                range: [yMax, 0],
              });

              // Ensure we have valid data
              if (massPerMinuteData.length === 0) {
                return (
                  <div className="FeedLinePerformanceChart-container">
                    <div>No data to display</div>
                  </div>
                );
              }

              return (
                <div className="FeedLinePerformanceChart-container">
                  <svg width={parent.width} height={chartHeight} onMouseMove={handleTooltip} onMouseLeave={hideTooltip}>
                    <Group left={chartMargin.left} top={chartMargin.top}>
                      <GridColumns
                        scale={xScale}
                        width={xMax}
                        height={yMax}
                        stroke="#D9DCE1"
                        tickValues={filteredTicks}
                        strokeDasharray="1,1"
                        left={0}
                        top={0}
                      />

                      {/* Bars for Auger Runs - render first so they appear behind lines */}
                      {showAugerRuns &&
                        augerRunsData.map((d, i) => {
                          const barWidth = (xMax / augerRunsData.length) * 0.4;
                          const barHeight = yMax - yScaleBars(getY(d));
                          const barX = xScale(getX(d)) - barWidth / 2;
                          const barY = yScaleBars(getY(d));

                          return (
                            <Bar
                              key={`bar-${i}`}
                              x={barX}
                              y={barY}
                              width={barWidth}
                              height={barHeight}
                              fill="rgba(163, 206, 182, 1.0)"
                              strokeWidth={1}
                            />
                          );
                        })}

                      {/* Line for Mass per Minute */}
                      {showMassPerMinute && (
                        <LinePath
                          data={massPerMinuteData}
                          x={(d) => xScale(getX(d))}
                          y={(d) => yScale(getY(d))}
                          stroke="#26AF5F"
                          strokeWidth={2}
                        />
                      )}

                      {/* Line for Running Time (secondary axis) */}
                      {showRunningTime && (
                        <LinePath
                          data={runningTimeData}
                          x={(d) => xScale(getX(d))}
                          y={(d) => yScaleSecondary(getY(d))}
                          stroke="#217BCE"
                          strokeWidth={2}
                          strokeDasharray="5,5"
                        />
                      )}

                      {/* X-axis */}
                      <AxisBottom
                        top={yMax}
                        scale={xScale}
                        hideTicks
                        stroke="none"
                        tickFormat={xAxisTickFormat}
                        tickValues={labelTicks}
                      />

                      {/* Primary Y-axis for mass per minute */}
                      <AxisLeft
                        scale={yScale}
                        hideTicks
                        stroke="none"
                        numTicks={5}
                        tickFormat={(value) => `${convertGramsToSmallUnits(isMetric, value)} ${unitLabel}/min`}
                      />

                      {/* Secondary Y-axis for running time */}
                      <AxisLeft
                        scale={yScaleSecondary}
                        hideTicks
                        stroke="none"
                        numTicks={5}
                        left={xMax + 10}
                        tickFormat={(value) => `${Math.round(value)} min`}
                      />

                      {/* Tooltip dots that appear at the tooltip position */}
                      {tooltipOpen && tooltipData && (
                        <>
                          {/* Highlight the corresponding bar */}
                          {showAugerRuns &&
                            augerRunsData.map((d, i) => {
                              if (Math.abs(d.t - tooltipData.timestamp) < 60 * 60 * 6) {
                                // Within 6 hours
                                const barWidth = (xMax / augerRunsData.length) * 0.4;
                                const barHeight = yMax - yScaleBars(getY(d));
                                const barX = xScale(getX(d)) - barWidth / 2;
                                const barY = yScaleBars(getY(d));

                                return (
                                  <rect
                                    key={`highlight-bar-${i}`}
                                    x={barX}
                                    y={barY}
                                    width={barWidth}
                                    height={barHeight}
                                    fill="rgba(34, 171, 95, 1.0)"
                                    //   stroke="#26AF5F"
                                    strokeWidth={2}
                                  />
                                );
                              }
                              return null;
                            })}
                          {/* Circle for Mass per Minute line */}
                          {showMassPerMinute && (
                            <circle
                              cx={xScale(tooltipData.timestamp)}
                              cy={yScale(tooltipData.massPerMinute)}
                              r={6}
                              fill="white"
                              stroke="#26AF5F"
                              strokeWidth={3}
                            />
                          )}
                          {/* Circle for Running Time line */}
                          {showRunningTime && (
                            <circle
                              cx={xScale(tooltipData.timestamp)}
                              cy={yScaleSecondary(tooltipData.duration / 60)}
                              r={6}
                              fill="white"
                              stroke="#217BCE"
                              strokeWidth={3}
                            />
                          )}
                        </>
                      )}
                    </Group>
                  </svg>
                  {tooltipOpen && tooltipData && (
                    <TooltipWithBounds key={Math.random()} left={tooltipLeft} top={tooltipTop} style={TOOLTIP_STYLES}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <strong>{dayjs(tooltipData.timestamp * 1000).format(DATE_FORMAT_WEEKDAY_MONTH_DAY)}</strong>
                        </div>
                        {showMassPerMinute && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{ width: '12px', height: '12px', backgroundColor: '#26AF5F', borderRadius: '2px' }}
                            ></div>
                            Mass/Min: {convertGramsToSmallUnits(isMetric, tooltipData.massPerMinute)}{' '}
                            {weightSmallUnitLabel(isMetric)}/min
                          </div>
                        )}
                        {showAugerRuns && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                backgroundColor: 'rgba(163, 206, 182, 1.0)',
                                borderRadius: '2px',
                              }}
                            ></div>
                            Auger Runs: {tooltipData.numAugerRuns}
                          </div>
                        )}
                        {showRunningTime && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{ width: '12px', height: '12px', backgroundColor: '#217BCE', borderRadius: '2px' }}
                            ></div>
                            Run Time: {Math.round(tooltipData.duration / 60)} min
                          </div>
                        )}
                      </div>
                    </TooltipWithBounds>
                  )}
                </div>
              );
            }}
          </ParentSize>
        </div>

        {/* Stats/Legend moved to the right */}
        <div className="FeedLinePerformanceChart-statsSidebar">
          <div className="FeedLinePerformanceChart-summary">{statsConfig.map(renderStatItem)}</div>
        </div>
      </div>
    </div>
  );
}

FeedLinePerformanceChart.propTypes = {
  feedLineId: PropTypes.string.isRequired,
  isMetric: PropTypes.bool,
  chartHeight: PropTypes.number,
  chartMargin: PropTypes.object,
  chartStartTime: PropTypes.number,
  chartEndTime: PropTypes.number,
  loading: PropTypes.bool,
};

export default FeedLinePerformanceChart;
